import { useMemo, type ReactElement } from 'react';
import {
  estimatePilaForWorkout,
  pilaBadgeClass,
  pilaLabel,
  type PilaDayEstimate,
  type PilaLevel,
} from '@/lib/nutrition';
import { useDashboard } from '@/lib/workouts';
import type { PlannedWorkout, WeekDay } from '@/lib/workouts';

/**
 * Vista "PILAS" del módulo Nutrición (M2.36).
 *
 * Explica el sistema PILAS I/II/III + muestra la pila estimada para los
 * próximos 7 días basándose en los workouts planificados del dashboard.
 *
 * Decisiones:
 *  - Reutilizamos `useDashboard()` (cliente ya cacheado por TanStack Query
 *    para el resto de la app); no es lo más eficiente — sólo necesitamos
 *    `week.days` — pero evita crear un endpoint paralelo.
 *  - La pila por día se estima con `estimatePilaForWorkout` (heurística
 *    front-only). Si el backend en el futuro expone `pilaLevel` por día
 *    en el meal plan, lo preferiremos.
 *  - Visualización del plato: `conic-gradient` CSS sin librería para
 *    representar la proporción aproximada del plato (verdura / proteína /
 *    CHO / grasa) de cada pila. Es decorativo; el detalle nutricional vive
 *    en el dossier (link externo si en el futuro lo añadimos).
 */
export function PilasInfoView(): ReactElement {
  return (
    <section aria-label="Sistema PILAS" className="space-y-6">
      <PilasHero />
      <UpcomingWeekEstimates />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hero — explicación de las 3 pilas
// ---------------------------------------------------------------------------

interface PilaDescriptor {
  pila: PilaLevel;
  title: string;
  subtitle: string;
  description: string;
  // Proporciones aproximadas del plato (suman 100): verdura · proteína · cho · grasa.
  plate: { veg: number; protein: number; cho: number; fat: number };
}

const PILAS: PilaDescriptor[] = [
  {
    pila: 1,
    title: 'PILA I',
    subtitle: 'Descanso o carga baja',
    description:
      'Días sin entreno o sesión muy suave (<30 min). Plato con énfasis en verdura, proteína moderada y carbohidratos bajos. Apoya control de inflamación y recuperación pasiva.',
    plate: { veg: 50, protein: 25, cho: 15, fat: 10 },
  },
  {
    pila: 2,
    title: 'PILA II',
    subtitle: 'Carga moderada',
    description:
      'Entrenos entre 30–75 min (rodajes, series cortas, fuerza). Equilibrio entre proteína (recuperación) y carbohidratos (reponer glucógeno).',
    plate: { veg: 30, protein: 30, cho: 30, fat: 10 },
  },
  {
    pila: 3,
    title: 'PILA III',
    subtitle: 'Carga alta o sesión larga',
    description:
      'Tiradas largas, series de calidad o sesiones >75 min. Carbohidratos altos (50–60% del plato), proteína alta para reparación, grasa suficiente para saciedad y antiinflamación (AOVE, pescado azul).',
    plate: { veg: 20, protein: 25, cho: 45, fat: 10 },
  },
];

function PilasHero(): ReactElement {
  return (
    <section
      aria-label="Las 3 pilas"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-lg font-semibold text-slate-900">
        ¿Qué son las PILAS?
      </h2>
      <p className="mt-1 text-sm text-slate-700">
        Sistema simple para adaptar el plato a la carga de entreno del día. La
        coach te asigna la pila que toca; tú aprendes a componer la comida.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        {PILAS.map((p) => (
          <PilaCard key={p.pila} descriptor={p} />
        ))}
      </div>
    </section>
  );
}

function PilaCard({ descriptor }: { descriptor: PilaDescriptor }): ReactElement {
  return (
    <article
      data-testid={`pila-card-${descriptor.pila}`}
      className="flex flex-col rounded-lg border border-slate-200 bg-slate-50/50 p-4 shadow-sm"
    >
      <header className="flex items-baseline justify-between gap-2">
        <h3
          className={`inline-flex rounded-full px-2 py-0.5 text-sm font-semibold ring-1 ${pilaBadgeClass(descriptor.pila)}`}
        >
          {descriptor.title}
        </h3>
        <p className="text-xs text-slate-600">{descriptor.subtitle}</p>
      </header>

      <div className="my-4 flex items-center justify-center">
        <PlateDiagram plate={descriptor.plate} pila={descriptor.pila} />
      </div>

      <p className="text-xs leading-relaxed text-slate-700">
        {descriptor.description}
      </p>

      <ul className="mt-3 space-y-1 text-xs text-slate-600">
        <PlateLegend label="Verdura" value={descriptor.plate.veg} color="bg-emerald-500" />
        <PlateLegend label="Proteína" value={descriptor.plate.protein} color="bg-rose-400" />
        <PlateLegend label="Carbohidrato" value={descriptor.plate.cho} color="bg-amber-400" />
        <PlateLegend label="Grasa (AOVE, etc.)" value={descriptor.plate.fat} color="bg-yellow-200" />
      </ul>
    </article>
  );
}

function PlateLegend({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}): ReactElement {
  return (
    <li className="flex items-center gap-2">
      <span aria-hidden="true" className={`inline-block h-2.5 w-2.5 rounded-sm ${color}`} />
      <span className="flex-1">{label}</span>
      <span className="font-medium text-slate-800">{value}%</span>
    </li>
  );
}

/**
 * Plato visual circular con `conic-gradient`. CSS-only, sin SVG/canvas para
 * mantener el bundle ligero. Los colores reflejan los grupos del plato.
 */
function PlateDiagram({
  plate,
  pila,
}: {
  plate: { veg: number; protein: number; cho: number; fat: number };
  pila: PilaLevel;
}): ReactElement {
  // Construimos el gradient con offsets acumulativos.
  const veg = plate.veg;
  const protein = plate.protein;
  const cho = plate.cho;
  const fat = plate.fat;
  const a = veg; // límite veg
  const b = a + protein;
  const c = b + cho;
  // El último (fat) llega hasta 100%.
  const gradient = `conic-gradient(
    #10b981 0% ${a}%,
    #fb7185 ${a}% ${b}%,
    #fbbf24 ${b}% ${c}%,
    #fde68a ${c}% 100%
  )`;
  return (
    <div
      role="img"
      aria-label={`Plato de ${pilaLabel(pila)}: ${veg}% verdura, ${protein}% proteína, ${cho}% carbohidrato, ${fat}% grasa`}
      style={{ background: gradient }}
      data-testid={`plate-${pila}`}
      className="h-28 w-28 rounded-full shadow-inner ring-2 ring-white"
    />
  );
}

// ---------------------------------------------------------------------------
// Próximos 7 días — estimación de pila por día
// ---------------------------------------------------------------------------

function UpcomingWeekEstimates(): ReactElement {
  const query = useDashboard();
  const days = query.data?.week?.days ?? [];

  const dayEstimates = useMemo(() => buildDayEstimates(days), [days]);

  return (
    <section
      aria-label="Pila estimada de los próximos 7 días"
      className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
    >
      <h2 className="text-lg font-semibold text-slate-900">Tu semana próxima</h2>
      <p className="mt-1 text-sm text-slate-700">
        Pila estimada para cada día según el workout principal previsto. Es una
        guía — la coach puede ajustar según cómo te sientas.
      </p>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="mt-4 text-sm text-slate-700">
          Cargando próximos días…
        </p>
      )}

      {query.isError && (
        <p
          role="alert"
          className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800"
        >
          No se pudo cargar el plan semanal.
        </p>
      )}

      {!query.isLoading && !query.isError && dayEstimates.length === 0 && (
        <p className="mt-4 rounded-md border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-600">
          Sin workouts planificados para esta semana.
        </p>
      )}

      {dayEstimates.length > 0 && (
        <ul
          role="list"
          className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7"
          data-testid="pilas-week-list"
        >
          {dayEstimates.map((d) => (
            <DayPilaCard
              key={d.dateISO}
              dateISO={d.dateISO}
              estimate={d.estimate}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

interface DayEstimate {
  dateISO: string;
  estimate: PilaDayEstimate;
}

function buildDayEstimates(days: WeekDay[]): DayEstimate[] {
  return days.map((d) => {
    const main = pickMainWorkout(d.workouts);
    if (!main) {
      return {
        dateISO: d.dateISO,
        estimate: estimatePilaForWorkout({
          durationMin: 0,
          discipline: 'descanso',
          intensityZone: null,
        }),
      };
    }
    return {
      dateISO: d.dateISO,
      estimate: estimatePilaForWorkout({
        durationMin: main.durationPlannedMin,
        discipline: main.discipline,
        intensityZone: main.intensityZone,
      }),
    };
  });
}

/** El workout más largo del día — heurística simple para "carga dominante". */
function pickMainWorkout(workouts: PlannedWorkout[]): PlannedWorkout | null {
  if (workouts.length === 0) return null;
  return workouts.reduce((best, w) =>
    w.durationPlannedMin > best.durationPlannedMin ? w : best,
  );
}

function DayPilaCard({
  dateISO,
  estimate,
}: {
  dateISO: string;
  estimate: PilaDayEstimate;
}): ReactElement {
  return (
    <li
      className="flex flex-col rounded-md border border-slate-200 bg-slate-50/40 p-3 shadow-sm"
      data-testid={`pila-day-${dateISO.slice(0, 10)}`}
    >
      <header className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          {dayShortLabel(dateISO)}
        </p>
        <p className="text-xs text-slate-500">{dayNumber(dateISO)}</p>
      </header>
      <p
        className={`mt-2 inline-flex w-fit rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${pilaBadgeClass(estimate.pila)}`}
      >
        {pilaLabel(estimate.pila)}
      </p>
      <p className="mt-2 text-xs text-slate-600">{estimate.reason}</p>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Date helpers (mismas semánticas que WeekMealPlanView pero locales — evita
// dependencia cross-file)
// ---------------------------------------------------------------------------

function dayShortLabel(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'UTC',
      weekday: 'short',
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function dayNumber(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      timeZone: 'UTC',
      day: '2-digit',
      month: 'short',
    }).format(new Date(iso));
  } catch {
    return '';
  }
}
